import React, { useContext, useEffect, useMemo } from 'react';
import { TranslationKey } from './../../../../../../../../types/translation/TranslationKey';
import {
  ProductTransaction,
  ProductTransactionStatus,
  ProductTransactionType,
} from '../../../../../../../../types/productTransaction';
import { Grid } from '@mui/material';
import DropdownSelect from '../../../../../../Common/DropdownSelect';
import { ProductMasterData } from '../../../../../../../../types/product_master_data/productMasterData';
import { testIds } from '../../../../../../../../util/identifiers/identifiers.util';
import { t } from '../../../../../../../../types/translation/Translator';
import { BinContext } from '../../../../../../../../context/BinContext';
import { OrderContext } from '../../../../../../../../context/OrderContext';
import { getQuantityByBinForProduct } from '../../../../../../../../util/product.util';
import { ProductContext } from '../../../../../../../../context/ProductContext';
import { BinStatusContext } from '../../../../../../../../context/BinStatusContext';
import { Container } from '../../../../../../../../types/container';
import { Bin } from '../../../../../../../../types/bin';
import { DropdownSearchSelect } from '../../../../../../../components/Dropdown';
import { Unilever } from '../../../../../../../../types/customer/DHL/Unilever/Unilever';
import { UnitOfMeasure } from '../../../../../../../../types/uom';
import { OrderType } from '../../../../../../../../types/order';
import { HandlingUnitContext } from '../../../../../../../../context/HandlingUnitContext';
import { ProductMasterDataContext } from '../../../../../../../../context/ProductMasterDataContext';

interface ProductTransactionSourceBinProps {
  transaction: ProductTransaction;
  setTransaction: React.Dispatch<React.SetStateAction<ProductTransaction>>;

  productMasterData?: ProductMasterData;
  container?: Container;

  disabled?: boolean;
}

export default function ProductTransactionSourceBin({
  transaction,
  setTransaction,
  productMasterData: pmd,
  container,
  disabled = false,
}: ProductTransactionSourceBinProps) {
  if (transaction.type === ProductTransactionType.inbound) return null;
  if (!pmd) return <ContainerTransactionSourceBin container={container} />;

  const { bins } = useContext(BinContext);
  const { orders } = useContext(OrderContext);
  const { products } = useContext(ProductContext);
  const { binStatuses } = useContext(BinStatusContext);
  const { handlingUnits } = useContext(HandlingUnitContext);
  const { productMasterData } = useContext(ProductMasterDataContext);

  const selectedHandlingUnit = useMemo(() => {
    return handlingUnits.get(transaction.product.handlingUnitId || '');
  }, [handlingUnits, transaction.product.handlingUnitId]);

  const sourceProductMasterDataId = useMemo(() => {
    return selectedHandlingUnit?.parentId || pmd.id;
  }, [selectedHandlingUnit, pmd.id]);

  const handlingUnitProductMasterData = useMemo(() => {
    if (!selectedHandlingUnit) return undefined;
    return productMasterData.get(selectedHandlingUnit.parentId);
  }, [selectedHandlingUnit]);

  const isDisposal = useMemo(() => {
    const order = transaction.parentId ? orders.get(transaction.parentId) : undefined;
    return order?.type === OrderType.disposal;
  }, [transaction.parentId, orders]);

  const binsForProduct = useMemo(() => {
    const quantityByBin = getQuantityByBinForProduct(
      [...(products.get(sourceProductMasterDataId)?.values() || [])],
      transaction.product.lotId,
    );

    return quantityByBin;
  }, [products, sourceProductMasterDataId, selectedHandlingUnit, transaction.product.lotId]);

  const allowedBinIds = useMemo(() => {
    const allBinIds = [...binsForProduct.keys()];

    const binIds = new Set();
    for (const binId of allBinIds) {
      const outboundAllowed = binStatuses.get(bins.get(binId)?.binStatusId || '')?.outboundAllowed ?? true;
      if ((isDisposal && !outboundAllowed) || (!isDisposal && outboundAllowed)) {
        binIds.add(binId);
      }
    }
    return binIds;
  }, [binsForProduct, transaction, binStatuses]);

  const sourceBinDisabled =
    transaction.status !== ProductTransactionStatus.created || disabled || !!transaction.containerTransactionId;

  const validBins = (
    Unilever.companyIds.includes(transaction.companyId)
      ? [...bins.values()].filter(b => b.stockLocationId === transaction.product.fromStockLocationId)
      : [...bins.values()].filter(
          b =>
            b.stockLocationId === transaction.product.fromStockLocationId &&
            binsForProduct.has(b.id) &&
            allowedBinIds.has(b.id),
        )
  ).sort(
    (a, b) =>
      (binsForProduct.get(b.id) || 0) - (binsForProduct.get(a.id) || 0) ||
      parseInt(a.name.match(/\d+/)?.[0] || '0', 10) - parseInt(b.name.match(/\d+/)?.[0] || '0', 10),
  );

  useEffect(() => {
    if (sourceBinDisabled) return;

    if (validBins.length === 1 && !transaction.product.fromBinId) {
      setTransaction(transaction => transaction.withFromBinId(validBins[0].id));
    }
  }, []);

  const displayQuantity = (quantity: number) => {
    if (!selectedHandlingUnit) {
      return UnitOfMeasure.toQuantityWithSuffix(pmd.unitOfMeasure, quantity.toString());
    }

    const multiplier = selectedHandlingUnit.quantity;
    const handlingUnitQuantity = UnitOfMeasure.toQuantityWithSuffix(
      handlingUnitProductMasterData?.unitOfMeasure,
      quantity.toString(),
    );
    const handledUnitQuantity = UnitOfMeasure.toQuantityWithSuffix(
      pmd.unitOfMeasure,
      (quantity * multiplier).toString(),
    );
    return `${handlingUnitQuantity} (${handledUnitQuantity})`;
  };

  return (
    <Grid item xs={6}>
      <DropdownSearchSelect<Bin>
        mandatory
        disabled={sourceBinDisabled}
        placeholder={t(TranslationKey.sourceBin).singular.label}
        label={t(TranslationKey.sourceBin).singular.label}
        items={validBins}
        value={bins.get(transaction.product.fromBinId || '')}
        textBuilder={item => item.name}
        itemBuilder={item => (
          <Grid container>
            <Grid item flexGrow={1}>
              <p>{item.name}</p>
            </Grid>
            <Grid item my={'auto'}>
              <p className='text-sm text-gray-400'>{displayQuantity(binsForProduct.get(item.id) || 0)}</p>
            </Grid>
          </Grid>
        )}
        onSelect={bin => {
          setTransaction(transaction => transaction.withFromBinId(bin?.id).withSerial(undefined).withLPN(undefined));
        }}
      />
    </Grid>
  );
}

interface ContainerTransactionSourceBinProps {
  container: Container | undefined;
}

function ContainerTransactionSourceBin({ container }: ContainerTransactionSourceBinProps) {
  if (!container) return null;

  const { bins } = useContext(BinContext);

  return (
    <Grid item xs={6}>
      <DropdownSelect<Bin>
        disabled
        toText={bin => bin.name}
        onChange={() => {}}
        selectedValue={bins.get(container.binId) || null}
        testId={testIds.bin}
        placeholder={t(TranslationKey.sourceBin).singular.label}
        label={t(TranslationKey.sourceBin).singular.label}
        values={[]}
      />
    </Grid>
  );
}
